/**
 * Theme choice: 'light' | 'dark' | 'system', kept in localStorage so it
 * survives sign-out and reloads (it is a device preference, not account
 * data). Applying it is one class on <html> - the palette swap lives
 * entirely in index.css.
 *
 * Two places change the theme now - the dock button on every page and the
 * Settings chooser - so the module also PUBLISHES the current choice.
 * Without that, opening Settings after using the dock would show a stale
 * selection until the page remounted.
 */

export type ThemeChoice = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'cgtodo-theme';
const media = window.matchMedia('(prefers-color-scheme: dark)');

const listeners = new Set<() => void>();

export function getThemeChoice(): ThemeChoice {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

/** What is actually on screen right now - 'system' resolved against the OS. */
export function isDarkNow(): boolean {
  const choice = getThemeChoice();
  return choice === 'dark' || (choice === 'system' && media.matches);
}

function apply(choice: ThemeChoice): void {
  const dark = choice === 'dark' || (choice === 'system' && media.matches);
  document.documentElement.classList.toggle('dark', dark);
}

function announce(): void {
  for (const fn of listeners) fn();
}

export function setThemeChoice(choice: ThemeChoice): void {
  if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, choice);
  apply(choice);
  announce();
}

/** Subscribe to theme changes. Returns the unsubscribe function. */
export function subscribeTheme(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Call once at boot: applies the stored choice and follows OS changes
 *  while the choice is "system". */
export function initTheme(): void {
  apply(getThemeChoice());
  media.addEventListener('change', () => {
    if (getThemeChoice() === 'system') {
      apply('system');
      // The choice did not change, but what is ON SCREEN did - the icon
      // in the dock has to follow.
      announce();
    }
  });
}
